import { useState } from "react";
import {
  RiArrowLeftLine,
  RiFileUploadLine,
  RiDownloadLine,
  RiBarChartBoxLine,
  RiPieChartLine,
  RiFileTextLine,
  RiCloseLine,
  RiCalendarLine,
  RiMailLine,
  RiAddLine,
  RiSaveLine,
} from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import LineChart from "../../components/billing/charts/LineChart";
import PieChart from "../../components/billing/charts/PieChart";

const FinancialReports = () => {
  const navigate = useNavigate();
  const [uploadedFiles, setUploadedFiles] = useState([
    {
      id: 1,
      name: "Q1_Financial_Report.pdf",
      size: "2.4 MB",
      uploadDate: "2024-02-20",
      type: "PDF",
    },
    {
      id: 2,
      name: "Annual_Revenue_2023.xlsx",
      size: "1.8 MB",
      uploadDate: "2024-02-15",
      type: "Excel",
    },
  ]);

  const [financialData] = useState({
    revenue: {
      total: 124500.0,
      growth: 15.8,
      monthly: [
        { month: "Jan", amount: 18500 },
        { month: "Feb", amount: 22000 },
        { month: "Mar", amount: 25500 },
      ],
    },
    expenses: {
      total: 78900.0,
      categories: [
        { name: "Operations", amount: 35000 },
        { name: "Marketing", amount: 15000 },
        { name: "Development", amount: 28900 },
      ],
    },
    profit: {
      total: 45600.0,
      margin: 36.6,
    },
  });

  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showCustomReportModal, setShowCustomReportModal] = useState(false);
  const [selectedMetrics, setSelectedMetrics] = useState([]);
  const [selectedVisualization, setSelectedVisualization] = useState("line");

  const revenueData = {
    labels: ["January", "February", "March", "April", "May", "June"],
    datasets: [
      {
        label: "Revenue",
        data: [12000, 19000, 15000, 25000, 22000, 30000],
        borderColor: "rgb(75, 192, 192)",
        tension: 0.1,
      },
    ],
  };

  const expenseData = {
    labels: ["Operations", "Marketing", "Development", "Sales", "Admin"],
    datasets: [
      {
        data: [35000, 15000, 28900, 12000, 8000],
        backgroundColor: [
          "rgba(255, 99, 132, 0.5)",
          "rgba(54, 162, 235, 0.5)",
          "rgba(255, 206, 86, 0.5)",
          "rgba(75, 192, 192, 0.5)",
          "rgba(153, 102, 255, 0.5)",
        ],
      },
    ],
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const newFiles = files.map((file, index) => ({
      id: Date.now() + index,
      name: file.name,
      size: `${(file.size / (1024 * 1024)).toFixed(1)} MB`,
      uploadDate: new Date().toISOString().split("T")[0],
      type: file.name.split(".").pop().toUpperCase(),
    }));

    setUploadedFiles([...uploadedFiles, ...newFiles]);
  };

  const removeFile = (id) => {
    setUploadedFiles(uploadedFiles.filter((file) => file.id !== id));
  };

  const ScheduleReportModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h2 className="text-xl font-semibold mb-4">Schedule Report</h2>
        <div className="space-y-4">
          <div>
            <label className="label">Report Type</label>
            <select className="input w-full">
              <option>Revenue Report</option>
              <option>Expense Report</option>
              <option>Profit & Loss</option>
            </select>
          </div>
          <div>
            <label className="label">Frequency</label>
            <select className="input w-full">
              <option>Daily</option>
              <option>Weekly</option>
              <option>Monthly</option>
            </select>
          </div>
          <div>
            <label className="label">Recipients</label>
            <input
              type="email"
              className="input w-full"
              placeholder="Enter email addresses"
            />
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <button
              className="btn btn-secondary"
              onClick={() => setShowScheduleModal(false)}
            >
              Cancel
            </button>
            <button className="btn btn-primary">Schedule Report</button>
          </div>
        </div>
      </div>
    </div>
  );

  const CustomReportBuilder = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-lg w-full">
        <h2 className="text-xl font-semibold mb-4">Custom Report Builder</h2>
        <div className="space-y-4">
          <div>
            <label className="label">Select Metrics</label>
            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <input type="checkbox" className="form-checkbox" />
                <span>Revenue</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" className="form-checkbox" />
                <span>Expenses</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" className="form-checkbox" />
                <span>Profit Margin</span>
              </label>
            </div>
          </div>
          <div>
            <label className="label">Visualization Type</label>
            <select
              className="input w-full"
              value={selectedVisualization}
              onChange={(e) => setSelectedVisualization(e.target.value)}
            >
              <option value="line">Line Chart</option>
              <option value="bar">Bar Chart</option>
              <option value="pie">Pie Chart</option>
            </select>
          </div>
          <div>
            <label className="label">Report Name</label>
            <input
              type="text"
              className="input w-full"
              placeholder="Enter report name"
            />
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <button
              className="btn btn-secondary"
              onClick={() => setShowCustomReportModal(false)}
            >
              Cancel
            </button>
            <button className="btn btn-primary">Save Template</button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-full mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/billing")}
            className="p-2 hover:bg-gray-100 rounded-lg text-gray-600 transition-colors"
          >
            <RiArrowLeftLine className="w-5 h-5" />
          </button>
          <h1 className="text-3xl font-bold text-gray-900">
            Financial Reports
          </h1>
        </div>
      </div>

      {/* Financial Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card bg-white">
          <div className="flex items-center gap-3">
            <RiBarChartBoxLine className="w-8 h-8 text-primary-600" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Revenue</h3>
              <p className="text-2xl font-bold text-primary-600">
                ${financialData.revenue.total.toLocaleString()}
              </p>
              <p className="text-sm text-green-600">
                +{financialData.revenue.growth}% growth
              </p>
            </div>
          </div>
        </div>
        <div className="card bg-white">
          <div className="flex items-center gap-3">
            <RiPieChartLine className="w-8 h-8 text-orange-600" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Expenses</h3>
              <p className="text-2xl font-bold text-orange-600">
                ${financialData.expenses.total.toLocaleString()}
              </p>
              <p className="text-sm text-gray-500">
                {financialData.expenses.categories.length} categories
              </p>
            </div>
          </div>
        </div>
        <div className="card bg-white">
          <div className="flex items-center gap-3">
            <RiBarChartBoxLine className="w-8 h-8 text-green-600" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Net Profit
              </h3>
              <p className="text-2xl font-bold text-green-600">
                ${financialData.profit.total.toLocaleString()}
              </p>
              <p className="text-sm text-gray-500">
                {financialData.profit.margin}% margin
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* File Upload Section */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">Uploaded Reports</h2>
          <label className="btn btn-primary flex items-center gap-2 cursor-pointer">
            <RiFileUploadLine className="w-5 h-5" />
            Upload Report
            <input
              type="file"
              className="hidden"
              multiple
              onChange={handleFileUpload}
              accept=".pdf,.xlsx,.csv,.doc,.docx"
            />
          </label>
        </div>

        <div className="space-y-4">
          {uploadedFiles.map((file) => (
            <div
              key={file.id}
              className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
            >
              <div className="flex items-center gap-3">
                <RiFileTextLine className="w-6 h-6 text-gray-400" />
                <div>
                  <h3 className="font-medium">{file.name}</h3>
                  <p className="text-sm text-gray-500">
                    {file.size} • Uploaded on{" "}
                    {new Date(file.uploadDate).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  className="p-2 hover:bg-gray-100 rounded-lg text-gray-600"
                  title="Download"
                >
                  <RiDownloadLine className="w-5 h-5" />
                </button>
                <button
                  className="p-2 hover:bg-gray-100 rounded-lg text-red-600"
                  title="Remove"
                  onClick={() => removeFile(file.id)}
                >
                  <RiCloseLine className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}

          {uploadedFiles.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <RiFileUploadLine className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p>No reports uploaded yet</p>
              <p className="text-sm">
                Upload your financial reports to get started
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Expense Categories */}
      <div className="card">
        <h2 className="text-xl font-semibold mb-4">Expense Breakdown</h2>
        <div className="space-y-4">
          {financialData.expenses.categories.map((category, index) => (
            <div key={index} className="flex items-center justify-between">
              <div>
                <h3 className="font-medium">{category.name}</h3>
                <div className="w-48 h-2 bg-gray-200 rounded-full mt-1">
                  <div
                    className="h-2 bg-primary-600 rounded-full"
                    style={{
                      width: `${(category.amount / financialData.expenses.total) * 100}%`,
                    }}
                  ></div>
                </div>
              </div>
              <p className="font-medium">${category.amount.toLocaleString()}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Monthly Revenue Chart */}
      <div className="card">
        <h2 className="text-xl font-semibold mb-4">Monthly Revenue</h2>
        <div className="h-64 flex items-end justify-between gap-2">
          {financialData.revenue.monthly.map((month, index) => (
            <div key={index} className="flex-1 flex flex-col items-center">
              <div
                className="w-full bg-primary-100 rounded-t"
                style={{
                  height: `${(month.amount / 30000) * 100}%`,
                }}
              ></div>
              <p className="text-sm text-gray-600 mt-2">{month.month}</p>
              <p className="text-sm font-medium">
                ${(month.amount / 1000).toFixed(1)}k
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4">
        <button
          className="btn btn-secondary flex items-center gap-2"
          onClick={() => setShowScheduleModal(true)}
        >
          <RiCalendarLine className="w-5 h-5" />
          Schedule Reports
        </button>
        <button
          className="btn btn-secondary flex items-center gap-2"
          onClick={() => setShowCustomReportModal(true)}
        >
          <RiAddLine className="w-5 h-5" />
          Create Custom Report
        </button>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h2 className="text-xl font-semibold mb-4">Revenue Trends</h2>
          <LineChart data={revenueData} title="Monthly Revenue" />
        </div>
        <div className="card">
          <h2 className="text-xl font-semibold mb-4">Expense Breakdown</h2>
          <PieChart data={expenseData} title="Expenses by Category" />
        </div>
      </div>

      {showScheduleModal && <ScheduleReportModal />}
      {showCustomReportModal && <CustomReportBuilder />}
    </div>
  );
};

export default FinancialReports;
